import moment from "moment";
import FollowUserButton from "components/FollowUserButton/FollowUserButton";
import Avatar from "hardcover-ui/components/Avatar";
import LibrarianFlair from "components/users/LibrarianFlar";
import ExpandingText from "components/ui/ExpandingText";
import BlockUserButton from "./BlockUserButton";
import EditProfileButton from "./EditProfileButton";
import { PageSerializersUsersShowPublic } from "types/serializers";
import { Link } from "@inertiajs/react";
import LocaleNumber from "components/ui/LocaleNumber";
import Heading from "hardcover-ui/components/Heading";
import Icon from "hardcover-ui/components/Icon";
import faLocationDot from "icons/solid/location-dot.svg";
import faLink from "icons/solid/link.svg";

export interface Props {
  user: PageSerializersUsersShowPublic;
}

export default function UserHeader({ user }: Props) {
  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start px-2 py-4 lg:py-6 xl:p-6 mb-2 md:mb-4">
      <div className="hidden sm:block flex-shrink-0 ">
        <Avatar
          image={user.image}
          size="2xl"
          alt={`@${user.username}`}
          className="shadow-lg"
        />
      </div>
      <div className="flex-grow w-full">
        <div className="flex items-center gap-3 ">
          <div className="flex-shrink-0 block sm:hidden">
            <Avatar
              image={user.image}
              size="xl"
              alt={`@${user.username}`}
              className="shadow-lg"
            />
          </div>
          <div className="flex-grow flex flex-row justify-between items-start flex-wrap gap-2">
            <div>
              <h3 className="lg:text-xl text-secondary-foreground opacity-80">
                @{user.username}
              </h3>
              <div className="flex flex-wrap gap-2 items-center">
                <Heading className="mt-1">{user.name}</Heading>
                <FollowUserButton user={user} />
              </div>
            </div>

            <div className="flex md:justify-end relative ">
              <div className="flex gap-2 flex-wrap justify-start sm:justify-end text-xs sm:text-sm">
                <div className="flex items-center divide-x divide-muted">
                  <span className="bg-secondary py-1 px-2 rounded-l-lg">
                    Joined
                  </span>
                  <span className="bg-secondary py-1 px-2 rounded-r-lg">
                    {moment.utc(user.createdAt).fromNow()}
                  </span>
                </div>

                <BlockUserButton user={user} />

                {user.flair && user.flair.length > 0 && (
                  <div className="text-sm hidden md:flex">
                    <span className="bg-secondary py-1 px-2 rounded-l-lg">
                      Flair
                    </span>
                    <span className="bg-accent py-1 px-2 rounded-r-lg text-accent-foreground font-semibold">
                      {user.flair}
                    </span>
                  </div>
                )}

                <LibrarianFlair roles={user.librarianRoles} />

                <EditProfileButton userId={user.id} />
              </div>
            </div>
          </div>
        </div>
        <div className="">
          {user.bio?.length > 0 ? (
            <ExpandingText text={user.bio} lines={4} className="my-2" />
          ) : (
            false
          )}

          <div className="flex gap-4 flex-wrap">
            <div className="flex gap-2 text-sm">
              <p className="flex gap-2 items-center">
                Followers
                <Link
                  href={`/@${user.username}/network/followers`}
                  className="flex bg-secondary hover:bg-accent hover:text-accent-foreground rounded-lg py-0.5 px-2 transition-colors"
                >
                  <LocaleNumber number={user.followersCount} />
                </Link>
              </p>
            </div>
            <div className="flex gap-2 text-sm">
              <p className="flex gap-2 items-center">
                Following
                <Link
                  href={`/@${user.username}/network/following`}
                  className="flex bg-secondary hover:bg-accent hover:text-accent-foreground rounded-lg py-0.5 px-2 transition-colors"
                >
                  {user.followedUsersCount.toLocaleString()}
                </Link>
              </p>
            </div>
          </div>

          <div className="flex gap-4 flex-wrap text-sm my-2">
            {user.location && user.location.length > 0 && (
              <p className="flex gap-1 items-center">
                <Icon icon={faLocationDot} className="text-secondary" />
                Location:
                <span className="truncate max-w-sm">{user.location}</span>
              </p>
            )}
            {user.link && user.link.length > 0 && (
              <p className="flex gap-1 items-center">
                <Icon icon={faLink} className="text-secondary" />
                Link:
                <a
                  href={user.link}
                  className="flex bg-secondary hover:bg-accent hover:text-accent-foreground rounded-lg py-0.5 px-2 transition-colors"
                  target="_blank"
                  rel="me nofollow noopener noreferrer"
                >
                  {user.link}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
